import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Tooltip,
  Button,
} from '@mui/material';
import { isEmpty, startCase } from 'lodash';
import { IActionRewardCampaign } from '../../reduxState/store/actionRewards/type';
import { Application } from 'reduxState/store/application/types';
import { actionRewardsHeaders, actionRewardDescriptions } from './constants';
import { formatLocalDateTime } from 'helpers/DateHelpers';
import { findAppNameFromId } from 'helpers/index';
import Loading from 'components/Loading/Loading';
import { getCampaignStatus } from './helpers';

/**
 * ActionRewardsTable component displays a table of action reward campaigns.
 *
 * @component
 * @param {Object} props
 * @param {IActionRewardCampaign[]} props.data - Array of action reward campaign objects to display.
 * @param {Application[]} props.applications - Array of application objects to display.
 * @param {boolean} props.isFetching - Indicates if the data is currently loading.
 * @param {number} props.totalPages - Total number of pages of data.
 * @param {function} props.setPage - Function to set the current page.
 *
 * @description
 * Renders a table with campaign details such as ID, action reward, bonus amount, currency, config, dates, and author information.
 * The config field is parsed from JSON and displayed as key-value pairs.
 *
 * @example
/**
 * <ActionRewardsTable
 *   data={campaigns}
 *   isFetching={false}
 *   totalPages={10}
 *   setPage={() => {}}
 *   setIsModalOpen={() => {}}
 *   setIsConfirmationModalOpen={() => {}}
 *   setSelectedCampaign={() => {}}
 * />
 */

const ActionRewardsTable = ({
  data,
  applications,
  isFetching,
  totalPages,
  setPage,
  setIsModalOpen,
  setSelectedCampaign,
  setIsConfirmationModalOpen,
}: {
  data: IActionRewardCampaign[];
  applications: Application[];
  isFetching: boolean;
  totalPages: number;
  setPage: (page: number) => void;
  setIsModalOpen: (isOpen: boolean) => void;
  setIsConfirmationModalOpen: (isOpen: boolean) => void;
  setSelectedCampaign: (campaign: IActionRewardCampaign) => void;
}) => {
  /**
   * Parses and formats the config JSON string for display.
   * @param {string} config - The config JSON string.
   */
  const getConfig = (config: string) => {
    if (!config || config.trim() === '') return '';

    try {
      const parsed = JSON.parse(config);

      if (isEmpty(parsed)) {
        return '';
      }

      return Object.entries(parsed).map(([key, value], index) => (
        <p key={index} className="font-montserrat text-xs">
          <span className="font-bold">{`${startCase(key)}: `}</span>
          {value?.toString() || ''}
        </p>
      ));
    } catch (error) {
      console.error('Failed to parse config:', error);
      return config || '';
    }
  };

  // Finds application name from application ID and returns app in `1 - Wildfire Demo` format
  const renderApplication = (applicationID: string) => {
    const applicationName = findAppNameFromId(applications, applicationID);
    return `${applicationID} - ${applicationName}`;
  };

  const handleEditCampaign = (campaign: IActionRewardCampaign) => {
    setSelectedCampaign(campaign);
    setIsModalOpen(true);
  };

  const handleDeleteCampaign = (campaign: IActionRewardCampaign) => {
    setSelectedCampaign(campaign);
    setIsConfirmationModalOpen(true);
  };

  const renderBody = () => {
    return data.map(campaign => {
      const campaignStatus = getCampaignStatus(campaign.startDate, campaign.endDate);
      return (
        <TableRow key={campaign.id}>
          <TableCell className="font-montserrat max-w-[300px] text-ellipsis p-3 whitespace-nowrap text-sm">
            {(campaignStatus === 'future' || campaignStatus === 'present') && (
              <Button
                className="px-2 py-1 bg-white border text-dark-purple rounded-3xl border-dark-purple border-solid font-montserrat normal-case hover:bg-dark-purple hover:text-white"
                onClick={() => handleEditCampaign(campaign)}
              >
                Edit
              </Button>
            )}
            {campaignStatus === 'future' && (
              <Button
                className="px-2 py-1 bg-white border text-rose-600 rounded-3xl border-rose-600 border-solid font-montserrat normal-case ml-2 hover:bg-rose-600 hover:text-white"
                onClick={() => handleDeleteCampaign(campaign)}
              >
                Delete
              </Button>
            )}
          </TableCell>
          <TableCell className="font-montserrat max-w-[300px] text-ellipsis p-3 whitespace-nowrap text-sm">
            {renderApplication(campaign.applicationID.toString())}
          </TableCell>
          <TableCell className="font-montserrat max-w-[300px] text-ellipsis p-3 whitespace-nowrap text-sm text-center">
            {campaign.id}
          </TableCell>
          <TableCell className="font-montserrat max-w-[300px] text-ellipsis p-3 whitespace-nowrap text-sm text-center">
            <Tooltip title={actionRewardDescriptions[campaign.actionReward] || campaign.actionReward} placement="top">
              <span className="cursor-help">{campaign.actionReward}</span>
            </Tooltip>
          </TableCell>
          <TableCell className="font-montserrat max-w-[300px] text-ellipsis p-3 whitespace-nowrap text-sm text-center">
            {parseFloat(campaign.bonusAmount).toFixed(2)}
          </TableCell>
          <TableCell className="font-montserrat max-w-[300px] text-ellipsis p-3 whitespace-nowrap text-sm">
            {campaign.campaignCode && (
              <p className="font-montserrat text-xs">
                <span className="font-bold">Campaign Code: </span>
                {campaign.campaignCode}
              </p>
            )}
            {getConfig(campaign.config)}
          </TableCell>
          <TableCell className="font-montserrat max-w-[300px] text-ellipsis p-3 whitespace-nowrap text-sm text-center">
            {formatLocalDateTime(campaign.startDate, 'M/dd/yyyy haaa')}
          </TableCell>
          <TableCell className="font-montserrat max-w-[300px] text-ellipsis p-3 whitespace-nowrap text-sm text-center">
            {formatLocalDateTime(campaign.endDate, 'M/dd/yyyy haaa')}
          </TableCell>
          <TableCell className="font-montserrat max-w-[300px] text-ellipsis p-3 whitespace-nowrap text-sm">
            {campaign.modifiedAuthor}
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <div className="w-full">
      <TableContainer className="table-container max-w-[1400px] overflow-auto text-center rounded-md shadow-lg">
        {isFetching ? (
          <Paper style={{ padding: '10px' }}>
            <Loading data-testid="loading" />
          </Paper>
        ) : (
          <Table className="bg-white">
            <TableHead
              className="sticky top-0 z-10 table-header-group bg-light-grey whitespace-nowrap"
              sx={{
                '& .MuiTableCell-root': {
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: 'var(--dark-grey)',
                  textWrap: 'nowrap',
                  textTransform: 'uppercase',
                  padding: '10px',
                  borderRight: '1px solid var(--dark-grey)',
                  backgroundColor: 'var(--light-grey)',

                  '&:last-of-type': {
                    border: 'none',
                  },
                },
              }}
            >
              <TableRow>
                {actionRewardsHeaders.map(header => (
                  <TableCell key={header.key}>{header.value}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>{renderBody()}</TableBody>
          </Table>
        )}
      </TableContainer>
      <Pagination className="flex justify-center mt-4" count={totalPages} onChange={(_, page) => setPage(page)} />
    </div>
  );
};

export default ActionRewardsTable;
